/*
 * soundOnAutoplay.js — deterministic (no LLM) per-request finding: a banner /
 * in-line video ad started playing WITH SOUND. Source of truth is the Liftoff
 * (Vungle) SDK's own telemetry: POST logs.ads.vungle.com/vm/logs carries one
 * event per post, with `is_muted:false` + `placement_type` In-Line/Banner/MRec
 * when the creative started unmuted. Interstitial/rewarded/app-open are skipped
 * (sound is normal there). Coalition for Better Ads lists auto-playing video
 * with sound as a prohibited mobile ad experience.
 *
 * Do NOT key on the word "mute" generally: GAM responses carry `mute_panel` /
 * `mute_survey_option` (Google's "mute this ad" feedback menu, not audio).
 *
 * detectSoundOnAutoplay(rowLike) → issues[] in the shared validator issue
 * shape ({field, message, note, level, severity, code}). Never throws.
 * Logcat rows never carry a body, so they return [].
 *
 * Server twin: proxy-server/adQuality/soundOnAutoplay.js — keep identical
 * (proxy-server/test/soundOnAutoplayParity.test.js). Used when a saved HAR is
 * opened so captures made before the server shipped the finding still show it.
 */

export const CODE = 'banner_autoplay_sound_on';
const PLACEMENT_RE = /^(in-?line|banner|mrec)$/i;

export function bodyText(row) {
    const b = row.requestBody != null ? row.requestBody
        : (row.postData != null ? row.postData : row.text);
    if (b == null) return null;
    if (typeof b === 'string') return b;
    if (typeof b === 'object' && typeof b.text === 'string') return b.text;
    if (typeof b === 'object') return b; // already parsed
    return null;
}

export function eventsOf(parsed) {
    if (Array.isArray(parsed)) return parsed;
    if (parsed && typeof parsed === 'object') {
        if (Array.isArray(parsed.events)) return parsed.events;
        return [parsed];
    }
    return [];
}

function detectSoundOnAutoplay(rowLike) {
    try {
        const row = rowLike || {};
        let u;
        try { u = new URL(String(row.url || '')); } catch { return []; }
        const host = u.hostname.toLowerCase();
        if (!(host === 'vungle.com' || host.endsWith('.vungle.com'))) return [];
        if (!/^\/vm\/logs\/?$/.test(u.pathname)) return [];

        let body = bodyText(row);
        if (body == null) return [];
        if (typeof body === 'string') {
            try { body = JSON.parse(body); } catch { return []; }
        }

        const issues = [];
        for (const ev of eventsOf(body)) {
            if (!ev || typeof ev !== 'object') continue;
            const m = ev.is_muted;
            if (!(m === false || m === 'false')) continue;
            const pt = typeof ev.placement_type === 'string' ? ev.placement_type.trim() : '';
            if (!PLACEMENT_RE.test(pt)) continue;

            const size = ev.width && ev.height ? ` ${ev.width}×${ev.height}` : '';
            const adv = ev.advertiser_domain ? `, ${ev.advertiser_domain}` : '';
            const message = `Banner ad autoplayed video with sound on (Liftoff, ${pt}${size}${adv})`;
            const ids = [
                ev.creative_id ? `creative_id ${ev.creative_id}` : null,
                ev.placement_reference_id ? `placement ${ev.placement_reference_id}` : null,
            ].filter(Boolean).join(', ');
            const note = 'Coalition for Better Ads lists auto-playing video with sound as a prohibited mobile ad experience.'
                + (ids ? ` Block/report in AdMob mediation or to Liftoff: ${ids}.` : '');
            issues.push({
                code: CODE,
                field: 'is_muted',
                message,
                note,
                level: 'error',
                severity: 'fail',
                source: 'adQuality',
            });
        }
        return issues;
    } catch (e) {
        return [];
    }
}

export { detectSoundOnAutoplay };
